import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Send, Phone, Mail, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { BUSINESS, SERVICE_OPTIONS } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  name: "",
  email: "",
  phone: "",
  service_type: SERVICE_OPTIONS[0],
  details: "",
  website: "", // honeypot — must stay empty
};

export const QuoteForm = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quote`, form);
      setDone(true);
      toast.success("Quote request sent! We'll be in touch shortly.");
      setForm(empty);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 429) {
        toast.error(
          err.response.data?.detail ||
            "Too many requests. Please try again shortly or call us."
        );
      } else {
        toast.error("Something went wrong. Please call us at " + BUSINESS.phone);
      }
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-[15px] text-slate-900 placeholder:text-slate-400 outline-none transition-colors focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20";

  return (
    <section id="quote" data-testid="quote-section" className="py-24 bg-slate-50 border-t border-slate-200">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-start">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-sm font-semibold uppercase tracking-[0.2em] text-[#2563eb]">
            Request a Quote
          </span>
          <h2 className="font-display mt-4 text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0f2452]">
            Let's get your space sparkling
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            Tell us a bit about what you need and we'll send you a free,
            no-obligation quote. Prefer to talk? Call or email us directly.
          </p>

          <div className="mt-8 space-y-4">
            <a
              href={BUSINESS.phoneHref}
              data-testid="quote-call-link"
              className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4 hover:border-[#2563eb]/40 transition-colors"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#0f2452] text-white">
                <Phone className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Call us
                </p>
                <p className="text-[15px] font-semibold text-[#0f2452]">
                  {BUSINESS.phone}
                </p>
              </div>
            </a>
            <a
              href={BUSINESS.emailHref}
              data-testid="quote-email-link"
              className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4 hover:border-[#2563eb]/40 transition-colors"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#0f2452] text-white">
                <Mail className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Email us
                </p>
                <p className="text-[15px] font-semibold text-[#0f2452]">
                  {BUSINESS.email}
                </p>
              </div>
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 shadow-lg"
        >
          {done ? (
            <div
              data-testid="quote-success"
              className="flex flex-col items-center text-center py-10"
            >
              <CheckCircle2 className="h-16 w-16 text-[#2563eb]" />
              <h3 className="font-display mt-5 text-2xl font-bold text-[#0f2452]">
                Thank you!
              </h3>
              <p className="mt-2 text-slate-600 max-w-sm">
                Your quote request has been received. Our team will get back to
                you as soon as possible.
              </p>
              <button
                onClick={() => setDone(false)}
                data-testid="quote-reset-button"
                className="mt-6 rounded-full bg-slate-100 px-6 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-200 transition-colors"
              >
                Send another request
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="quote-form" className="space-y-4">
              {/* Honeypot field — hidden from real users, bots tend to fill it */}
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={update("website")}
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: "-9999px",
                  width: "1px",
                  height: "1px",
                  opacity: 0,
                }}
              />
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                    Full name *
                  </label>
                  <input
                    data-testid="quote-input-name"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Jane Doe"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                    Phone *
                  </label>
                  <input
                    data-testid="quote-input-phone"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="709-000-0000"
                    className={inputCls}
                  />
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                  Email *
                </label>
                <input
                  data-testid="quote-input-email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@example.com"
                  className={inputCls}
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                  Service needed
                </label>
                <select
                  data-testid="quote-select-service"
                  value={form.service_type}
                  onChange={update("service_type")}
                  className={inputCls}
                >
                  {SERVICE_OPTIONS.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                  Details
                </label>
                <textarea
                  data-testid="quote-input-details"
                  value={form.details}
                  onChange={update("details")}
                  rows={4}
                  placeholder="Tell us about the space, square footage, timing, etc."
                  className={inputCls + " resize-none"}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="quote-submit-button"
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#2563eb] px-6 py-4 text-base font-semibold text-white hover:bg-[#1d4ed8] transition-colors disabled:opacity-60 shadow-md shadow-blue-900/20"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" /> Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5" /> Request Free Quote
                  </>
                )}
              </button>
              <p className="text-center text-xs text-slate-400">
                We'll never share your information. Response within 1 business day.
              </p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
